import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  SimpleSourceList,
  StandardsSourceListWithOverride,
  NewsSourceList,
  type SourceItem,
} from '@/shared/subscriptions';
import { Loader2, Shield, Compass, Gem, Megaphone } from 'lucide-react';
import { useState, useEffect, useRef, type FC } from 'react';

type CategoryKey = 'regulations' | 'guidances' | 'standards' | 'news';

type CategoryData = {
  sources: SourceItem[];
  totalCount: number;
  recommendedCount: number;
};

export type OnboardingResultsTabbedProps = {
  regulationsData: CategoryData | null;
  guidancesData: CategoryData | null;
  standardsData: CategoryData | null;
  newsData: CategoryData | null;
  regulationsLoading: boolean;
  guidancesLoading: boolean;
  standardsLoading: boolean;
  newsLoading: boolean;
  selectedSourceIds: Set<string>;
  onToggleSource: (id: string) => void;
};

const CATEGORY_CONFIG: Record<
  CategoryKey,
  {
    label: string;
    sourceType: string;
    Icon: FC<{ className?: string }>;
    bgClass: string;
    loadingMessages: string[];
    description: (total: number, recommended: number) => string;
  }
> = {
  regulations: {
    label: 'Regulation',
    sourceType: 'regulation',
    Icon: Shield,
    bgClass: 'bg-[#fadff7]',
    loadingMessages: [
      'Scanning regulatory databases...',
      'Analyzing official regulations...',
      'Evaluating against company profile...',
    ],
    description: (total, rec) =>
      `Of ${total} official regulations, we found ${rec} relevant to you.`,
  },
  guidances: {
    label: 'Guideline',
    sourceType: 'guidance',
    Icon: Compass,
    bgClass: 'bg-[#d8dcff]',
    loadingMessages: [
      'Scanning regulatory databases...',
      'Analyzing official guidances...',
      'Evaluating against company profile...',
    ],
    description: (total, rec) =>
      `Of ${total} official guidances, we found ${rec} relevant to you.`,
  },
  standards: {
    label: 'Standard',
    sourceType: 'standard',
    Icon: Gem,
    bgClass: 'bg-[#ffe7c9]',
    loadingMessages: [
      'Scanning standards databases...',
      'Analyzing official standards...',
      'Evaluating against company profile...',
    ],
    description: (total, rec) =>
      `Of ${total} official standards, we found ${rec} relevant to you.`,
  },
  news: {
    label: 'News',
    sourceType: 'news',
    Icon: Megaphone,
    bgClass: 'bg-[#d8e9ff]',
    loadingMessages: [
      'Scanning regulatory news databases...',
      'Analyzing related news sources...',
      'Evaluating against company profile...',
    ],
    description: (total, rec) =>
      `Of ${total} news sources, we found ${rec} relevant to you.`,
  },
};

const CATEGORIES: CategoryKey[] = [
  'regulations',
  'guidances',
  'standards',
  'news',
];

type OverviewTab = 'overview';
type TabKey = OverviewTab | CategoryKey;

// Hook for fake progress animation on a card
function useCardProgress(loading: boolean) {
  const [progress, setProgress] = useState(0);
  const [completedLines, setCompletedLines] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!loading) {
      setProgress(100);
      setCompletedLines(3);
      return;
    }

    setProgress(0);
    setCompletedLines(0);

    // Line strikethrough timers: ~3s, ~7s, ~12s
    const lineTimers = [
      setTimeout(() => setCompletedLines(1), 2500 + Math.random() * 1500),
      setTimeout(() => setCompletedLines(2), 6000 + Math.random() * 2000),
    ];

    // Progress bar: advance in random increments
    let current = 0;
    const tick = () => {
      const increment = 2 + Math.random() * 6;
      current = Math.min(current + increment, 85);
      setProgress(current);
      timerRef.current = setTimeout(tick, 300 + Math.random() * 700);
    };
    timerRef.current = setTimeout(tick, 200);

    return () => {
      lineTimers.forEach(clearTimeout);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [loading]);

  return { progress, completedLines };
}

const OverviewCard: FC<{
  category: CategoryKey;
  data: CategoryData | null;
  loading: boolean;
  progress: number;
  completedLines: number;
}> = ({ category, data, loading, progress, completedLines }) => {
  const config = CATEGORY_CONFIG[category];
  const { Icon } = config;
  const isLoaded = !loading && data !== null;

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 flex flex-col gap-4 shadow-sm">
      <span
        className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 self-start ${config.bgClass}`}
      >
        <Icon className="size-3.5" />
        {config.label}
      </span>

      {isLoaded ? (
        <div className="flex flex-col gap-2">
          <span className="text-5xl font-semibold leading-none text-neutral-900 tracking-tight">
            {data.recommendedCount}
          </span>
          <p className="text-base text-neutral-900 leading-relaxed">
            {config.description(data.totalCount, data.recommendedCount)}
          </p>
        </div>
      ) : loading ? (
        <div className="flex flex-col gap-1.5">
          {config.loadingMessages.map((msg, i) => (
            <p
              key={i}
              className={`text-xs text-neutral-500 transition-all duration-300 ${
                i < completedLines ? 'line-through' : ''
              }`}
            >
              {msg}
            </p>
          ))}
          <div className="mt-2 h-2 w-full rounded-full border border-neutral-200 bg-neutral-100 overflow-hidden">
            <div
              className="h-full bg-neutral-900 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-neutral-500">Failed to load. Try again.</p>
        </div>
      )}
    </div>
  );
};

export const OnboardingResultsTabbed: FC<OnboardingResultsTabbedProps> = ({
  regulationsData,
  guidancesData,
  standardsData,
  newsData,
  regulationsLoading,
  guidancesLoading,
  standardsLoading,
  newsLoading,
  selectedSourceIds,
  onToggleSource,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  const dataMap: Record<CategoryKey, CategoryData | null> = {
    regulations: regulationsData,
    guidances: guidancesData,
    standards: standardsData,
    news: newsData,
  };

  const loadingMap: Record<CategoryKey, boolean> = {
    regulations: regulationsLoading,
    guidances: guidancesLoading,
    standards: standardsLoading,
    news: newsLoading,
  };

  const regulationsProgress = useCardProgress(regulationsLoading);
  const guidancesProgress = useCardProgress(guidancesLoading);
  const standardsProgress = useCardProgress(standardsLoading);
  const newsProgress = useCardProgress(newsLoading);

  const progressMap: Record<
    CategoryKey,
    { progress: number; completedLines: number }
  > = {
    regulations: regulationsProgress,
    guidances: guidancesProgress,
    standards: standardsProgress,
    news: newsProgress,
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as TabKey)}
        className="flex flex-col items-center"
      >
        <TabsList className="h-10 bg-neutral-100 p-1 rounded-xl">
          <TabsTrigger
            value="overview"
            className="rounded-[10px] min-h-[32px] px-2.5 py-[5.5px] text-sm font-medium"
          >
            Overview
          </TabsTrigger>
          {CATEGORIES.map(cat => {
            const config = CATEGORY_CONFIG[cat];
            const isLoading = loadingMap[cat];
            const isReady = !isLoading && dataMap[cat] !== null;
            return (
              <TabsTrigger
                key={cat}
                value={cat}
                disabled={!isReady}
                className="rounded-[10px] min-h-[32px] px-2.5 py-[5.5px] text-sm font-medium gap-1.5 disabled:opacity-40"
              >
                <config.Icon className="size-4" />
                {config.label}
                {isLoading && (
                  <Loader2 className="size-3.5 animate-spin text-neutral-400" />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-2 gap-4">
          {CATEGORIES.map(cat => (
            <OverviewCard
              key={cat}
              category={cat}
              data={dataMap[cat]}
              loading={loadingMap[cat]}
              progress={progressMap[cat].progress}
              completedLines={progressMap[cat].completedLines}
            />
          ))}
        </div>
      )}

      {activeTab === 'regulations' && regulationsData && (
        <div className="flex flex-col *:!overflow-visible">
          <SimpleSourceList
            sources={regulationsData.sources}
            selectedIds={selectedSourceIds}
            onToggle={onToggleSource}
            searchPlaceholder="Search regulations"
          />
        </div>
      )}

      {activeTab === 'guidances' && guidancesData && (
        <div className="flex flex-col *:!overflow-visible">
          <SimpleSourceList
            sources={guidancesData.sources}
            selectedIds={selectedSourceIds}
            onToggle={onToggleSource}
            searchPlaceholder="Search guidances"
          />
        </div>
      )}

      {activeTab === 'standards' && standardsData && (
        <div className="flex flex-col *:!overflow-visible">
          <StandardsSourceListWithOverride
            sources={standardsData.sources}
            selectedIds={selectedSourceIds}
            onToggle={onToggleSource}
          />
        </div>
      )}

      {activeTab === 'news' && newsData && (
        <div className="flex flex-col *:!overflow-visible">
          <NewsSourceList
            sources={newsData.sources}
            selectedIds={selectedSourceIds}
            onToggle={onToggleSource}
          />
        </div>
      )}
    </div>
  );
};
